import React, { useContext } from 'react';
import moment from 'moment';
import { styled } from '@mui/material';

import { LinkedTemplatesSection } from 'components/organisms/Milestones';
import { useSnackbar } from 'contexts/snackbar';
import { useUser2 } from 'contexts/user2';
import { PageContext } from 'pages/TaskManager/PageProvider';
import {
    Box,
    Button,
    DatePicker,
    Divider,
    FormLabel,
    Icon,
    MenuItem,
    Radio,
    RadioGroup,
    ResourceChip,
    RichTextArea,
    Select,
    Stack,
    Switch,
    Tag,
    Text,
    TextInput,
} from 'shell/components';
import { SUBMISSION_TYPES, TASK_WORKLOAD_OPTIONS } from 'utils/constants';
import { downloadFileFromURL, getAbsoluteURL, getS3FileURL } from 'utils/helper';

const getSubtitleByState = (milestoneState, milestone) => {
    switch (milestoneState) {
        case 'draft':
            return `Draft created on ${moment(milestone.created_on).format('MMMM D[,] YYYY')}`;
        case 'published':
            return `Published on ${moment(milestone.created_on).format('MMMM D[,] YYYY')}`;
        case 'scheduled':
            return `Scheduled to Publish on ${moment(milestone.published_on).format('MMMM D[,] YYYY')}`;
    }
};

const MilestoneView = ({
    milestone,
    milestoneState,
    files,
    sharedUrls,
    changes,
    updateMilestone,
    onAddResources,
    onDeleteResource,
    onClickTemplate,
    onUnlinkTemplate,
    onCreateTemplate,
}) => {
    const { message } = useSnackbar();
    const { formatives } = useUser2();
    const { setConfirmationModal, submissions, setFormativeDialog } = useContext(PageContext);

    const workloadOptions = TASK_WORKLOAD_OPTIONS.map((option) => ({
        key: option.value,
        value: <Tag sx={{ width: '100%' }} type={'workload'} value={option.value} />,
    }));

    const fileResources = Object.values(files)
        .filter((file) => file.active && file.milestone_id === milestone.id)
        .map((file) => ({ ...file, origin: file.type, type: 'file' }));
    const sharedUrlResources = Object.values(sharedUrls)
        .filter((sharedUrl) => sharedUrl.active && sharedUrl.milestone_id === milestone.id)
        .map((sharedUrl) => ({ ...sharedUrl, origin: sharedUrl.type, type: 'link' }));
    const resources = [...fileResources, ...sharedUrlResources].sort((a, b) => a.created_on - b.created_on);

    milestone = { ...milestone, ...changes };

    const closeConfirmationModal = () => {
        setConfirmationModal((current) => ({
            ...current,
            visible: false,
            onConfirm: () => {},
            onCancel: () => {},
        }));
    };

    const handleSelect = ({ key }, resource) => {
        switch (key) {
            case 'download':
                downloadFileFromURL(getS3FileURL(resource.data), resource.data.name);
                break;
            case 'visit':
                downloadFileFromURL(getAbsoluteURL(resource.url));
                break;
            case 'copy':
                navigator.clipboard.writeText(getAbsoluteURL(resource.url));
                message.success('Link copied to clipboard.');
                break;
            case 'delete':
                onDeleteResource(resource.type, resource.id);
                break;
            default:
                break;
        }
    };

    const renderWorkload = () => {
        if (!milestone.workload) return null;
        return (
            <>
                <CustomFormLabel htmlFor={'milestone-workload'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Workload
                    </Text>
                </CustomFormLabel>
                <CustomSelect
                    inputProps={{ id: 'milestone-workload' }}
                    renderValue={(selected) => {
                        return workloadOptions.find(({ key }) => key === selected).value;
                    }}
                    value={milestone.workload}
                    onChange={(e) => updateMilestone('workload', e.target.value)}
                >
                    {workloadOptions.map(({ key, value }) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </CustomSelect>
            </>
        );
    };

    const renderDueDate = () => {
        return (
            <>
                <CustomFormLabel htmlFor={'milestone-datepicker'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Due Date
                    </Text>
                </CustomFormLabel>
                <CustomDatePicker
                    id={'milestone-datepicker'}
                    value={(milestone.end_on && moment(milestone.end_on)) || moment()}
                    onChange={(timestamp) => updateMilestone('end_on', timestamp.valueOf())}
                />
            </>
        );
    };

    const renderDetails = () => {
        return (
            <>
                <FormLabel sx={{ mt: '10px' }} htmlFor={'milestone-description'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Details
                    </Text>
                </FormLabel>
                <RichTextArea
                    id={'milestone-description'}
                    placeholder={'Enter Task Details'}
                    value={milestone.description || ''}
                    variant={'dynamic'}
                    onChange={(html) => updateMilestone('description', html)}
                />
            </>
        );
    };

    const renderResources = () => {
        return (
            <>
                <FormLabel sx={{ mt: '10px' }} htmlFor={'milestone-resources'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        Resources
                    </Text>
                </FormLabel>
                <Stack sx={{ minWidth: 0 }} alignItems={'flex-start'} spacing={2}>
                    {resources.length ? (
                        <Stack sx={{ flexWrap: 'wrap', maxWidth: '100%' }} spacing={2}>
                            {resources.map((resource) => {
                                return (
                                    <ResourceChip
                                        key={resource.id}
                                        options={[
                                            ...(resource.type === 'file'
                                                ? [
                                                      { key: 'download', value: 'Download' },
                                                      { key: 'delete', value: 'Delete' },
                                                  ]
                                                : [
                                                      { key: 'visit', value: 'Visit Link' },
                                                      { key: 'copy', value: 'Copy Link' },
                                                      { key: 'delete', value: 'Delete' },
                                                  ]),
                                        ]}
                                        resource={resource}
                                        size={'small'}
                                        type={resource.type}
                                        onSelect={(option) => handleSelect(option, resource)}
                                    />
                                );
                            })}
                        </Stack>
                    ) : null}
                    <Button
                        data-cy={'add-resources-button'}
                        startIcon={<Icon kind={'add'} />}
                        variant={'tertiary'}
                        onClick={onAddResources}
                    >
                        Add Resources
                    </Button>
                </Stack>
            </>
        );
    };

    const renderSubmissionTypeSection = () => {
        if (!milestone.submission_type) return null;
        const disabled = submissions?.length !== 0;
        return (
            <Stack spacing={1}>
                <Text variant={'p2'} weight={'semibold'}>
                    Submission Type
                </Text>
                <RadioGroup
                    value={milestone.submission_type}
                    onChange={(e) => updateMilestone('submission_type', e.target.value)}
                >
                    <Radio disabled={disabled} label={'File Upload'} value={SUBMISSION_TYPES.FILE} />
                    <Radio disabled={disabled} label={'Text Entry'} value={SUBMISSION_TYPES.PLAINTEXT} />
                    <Radio disabled={disabled} label={'Website URL'} value={SUBMISSION_TYPES.URL} />
                </RadioGroup>
            </Stack>
        );
    };

    const renderAIFeedbackSection = () => {
        const formative = formatives?.find(({ milestone_id }) => milestone_id === milestone.id);

        const renderFormativeStatusIndicator = (formative) => {
            if (!formative) return null;

            const getFormativeState = (status) => {
                return {
                    complete: {
                        color: (theme) => theme.palette.ensightful.success[700],
                        iconKind: 'checkmark',
                        statusText: 'Setup Complete',
                    },
                    draft: {
                        color: (theme) => theme.palette.ensightful.warning[600],
                        iconKind: 'clock',
                        statusText: 'Setup in progress',
                    },
                }[status];
            };

            const { color, iconKind, statusText } = getFormativeState(formative.status);

            return (
                <Stack sx={{ mb: 1, color }} alignItems={'center'} direction={'row'} spacing={1}>
                    <Icon kind={iconKind} />
                    <Text color={'inherit'}>{statusText}</Text>
                </Stack>
            );
        };

        return (
            <Stack>
                {renderFormativeStatusIndicator(formative)}
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'p2'} weight={'semibold'}>
                        AI Formative Feedback
                    </Text>
                    <Switch
                        aria-label={'toggle formative feedback'}
                        checked={Boolean(milestone.allow_ai_feedback)}
                        onChange={(_, checked) => {
                            if (!milestone.isLocal && !checked) {
                                setConfirmationModal({
                                    visible: true,
                                    destructive: true,
                                    title: 'Disable AI Formative Feedback?',
                                    content:
                                        'Students will immediately lose access to this feature if it is disabled, including feedback they have already received.',
                                    confirmText: 'Disable',
                                    onConfirm: () => {
                                        closeConfirmationModal();
                                        updateMilestone('allow_ai_feedback', checked);
                                    },
                                    onCancel: closeConfirmationModal,
                                });
                            } else {
                                updateMilestone('allow_ai_feedback', checked);
                            }
                        }}
                    />
                </Stack>
                <Text sx={{ mt: 2, mb: 3 }}>
                    Provides personalized and timely feedback to students, tailored to the assignment.
                </Text>
                {milestone.allow_ai_feedback && (
                    <Button
                        sx={{ width: 'fit-content', height: '2.25rem' }}
                        variant={'borderless'}
                        onClick={() => setFormativeDialog({ open: true })}
                    >
                        {formative?.status === 'complete' && 'Edit Settings'}
                        {formative?.status === 'draft' && 'Continue Setup'}
                        {!formative && 'Start Setup'}
                    </Button>
                )}
            </Stack>
        );
    };

    return (
        <>
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <TitleTextInput
                    data-cy={'milestone-title'}
                    placeholder={'Enter Milestone Title'}
                    value={milestone.title || ''}
                    onChange={(e) => updateMilestone('title', e.target.value)}
                />
                <Tag type={'task'} value={'milestone'} />
            </Stack>
            {!milestone.isLocal ? (
                <Text color={'light'} variant={'p2'}>
                    {getSubtitleByState(milestoneState, milestone)}
                </Text>
            ) : null}
            <TaskInfoBox>
                {renderWorkload()}
                {renderDueDate()}
                {renderDetails()}
                {renderResources()}
            </TaskInfoBox>
            <Divider />
            {renderSubmissionTypeSection()}
            <Divider />
            {renderAIFeedbackSection()}
            <Divider />
            <LinkedTemplatesSection
                milestone={milestone}
                onClickTemplate={onClickTemplate}
                onUnlinkTemplate={onUnlinkTemplate}
                onCreateTemplate={onCreateTemplate}
            />
        </>
    );
};

const TitleTextInput = styled(TextInput)(({ theme }) => {
    return {
        flex: 1,
        marginLeft: theme.spacing(-3),
        '&& input': {
            fontSize: `${theme.typography.sh1.fontSize.large}rem`,
            fontWeight: theme.typography.sh1.fontWeight.semibold,
            letterSpacing: `${theme.typography.sh1.letterSpacing.large}rem`,
            lineHeight: `${theme.typography.getLineHeight('sh1', 'large')}rem`,
        },
        '& fieldset': {
            border: 'none',
        },
    };
});

const TaskInfoBox = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '5.625rem 1fr',
        gridGap: theme.spacing(1, 0),
        width: '100%',
    };
});

const CustomFormLabel = styled(FormLabel)(() => {
    return {
        alignSelf: 'center',
    };
});

const CustomSelect = styled(Select)(() => {
    return {
        minWidth: 0,
        maxWidth: 'fit-content',
        '& fieldset': {
            border: 'none',
        },
    };
});

const CustomDatePicker = styled(DatePicker)(() => {
    return {
        '& fieldset': {
            border: 'none',
        },
    };
});

export default MilestoneView;
